import { VisualRunRateType } from '@snap-types/models/visual/operation/enums';
import { Decimal, ZERO } from '@snap-types/types/decimal';

import type { QuantityAwareOperation } from '../types';

type Input = QuantityAwareOperation & {
  quantity: Decimal;
};

/**
 * The hours an operation contributes to the shop floor, kept separate so the
 * lead time breakdown can show its parts. Null means the operation has no such
 * input at all, as opposed to contributing zero hours.
 *
 * Transit days are not here. They are not shop floor time and do not convert at
 * the site's shift length — they wait on a carrier's week, which is why they
 * reach the total as their own term rather than as hours.
 *
 * Run hours carry the operation's multiplier, the lever the quoting team has
 * for stretching or shrinking one operation without touching the resource every
 * routing on the floor shares. Setup is untouched by it.
 *
 * @note Only `PCS/HR` run rates produce run hours. This is to spec — the other
 * nine `VisualRunRateType` members contribute setup only, because the site
 * using this strategy quotes its routings in `PCS/HR`.
 *
 * `QUANTITY_AWARE` is offered to the `cap` site alone, in
 * `leadTimeConfigTypesForSite` (ui `LeadTimeConfigForm/data.ts`), which is what
 * keeps that safe. The gate is UI-only — the database enum accepts the value
 * for any site — so adding another site to that map silently inherits
 * "operations at any other rate cost zero run hours". Revisit this branch
 * before doing so.
 */
export function calculateOperationHours({
  quantity,
  runType,
  run,
  setupHours,
  runHoursMultiplier,
}: Input) {
  return {
    runHours:
      runType === VisualRunRateType.PCS_PER_HR && run && run.gt(ZERO)
        ? quantity.dividedBy(run).times(runHoursMultiplier)
        : null,
    setupHours,
  };
}
